import { useEffect, useState } from "react";
import { api } from "../api";
import type { HistoryEntry, RewindProposal, Session } from "../types";

interface Props {
  session: Session;
  currentVersion: number;
  onClose: () => void;
  onChanged: () => void;
}

export function RecoveryPanel({ session, currentVersion, onClose, onChanged }: Props) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [proposal, setProposal] = useState<RewindProposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const load = async () => {
    try {
      const [entries, active] = await Promise.all([api.history(session), api.recovery(session)]);
      setHistory(entries);
      setProposal(active);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Geschiedenis laden mislukt");
    }
  };
  useEffect(() => {
    void load();
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const propose = async (version: number) => {
    setBusy(true); setError(undefined);
    try {
      setProposal(await api.proposeRecovery(session, version));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Voorstel mislukt");
    } finally { setBusy(false); }
  };
  const resolve = async (accept: boolean) => {
    if (!proposal) return;
    setBusy(true); setError(undefined);
    try {
      await api.resolveRecovery(session, proposal.proposal_id, accept);
      setProposal(null);
      await load();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Herstelbesluit mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-layer"><section className="modal recovery-modal" role="dialog" aria-modal="true" aria-labelledby="recovery-title">
      <header className="modal-titlebar"><div><span className="eyebrow">Veilige tijdlijn</span><h2 id="recovery-title">Geschiedenis & herstel</h2></div><button className="icon-button" onClick={onClose} aria-label="Sluiten">×</button></header>
      <p>Iedere bevestigde actie is een controleerbare revisie. Teruggaan kan alleen wanneer de andere speler akkoord gaat; de herstelde stand opent gepauzeerd.</p>
      {proposal ? (
        <div className="proposal-card">
          <strong>Open voorstel: terug naar revisie {proposal.target_version}</strong>
          <p>{proposal.requested_by === session.seat ? "Wacht op akkoord van de andere speler." : "De andere speler vraagt je dit herstelpunt te controleren."}</p>
          {proposal.requested_by !== session.seat ? <div className="button-row"><button disabled={busy} onClick={() => resolve(false)}>Afwijzen</button><button className="danger" disabled={busy} onClick={() => resolve(true)}>Akkoord en herstellen</button></div> : null}
        </div>
      ) : null}
      {error ? <div className="inline-error">{error}</div> : null}
      <ol className="history-list">
        {history.map((entry) => (
          <li key={entry.version} className={entry.is_current ? "current" : ""}>
            <span className="history-version">v{entry.version}</span>
            <div><strong>{entry.summary}</strong><small>{new Date(entry.created_at).toLocaleString("nl-NL")} · {entry.actor || "server"}</small><code title={entry.state_hash}>{entry.state_hash.slice(0, 10)}</code></div>
            {!proposal && entry.version > 0 && entry.version < currentVersion ? <button disabled={busy} onClick={() => propose(entry.version)}>Herstel voorstellen</button> : null}
          </li>
        ))}
      </ol>
    </section></div>
  );
}
